import type { FC, ReactNode, RefObject } from 'react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import type { AnimalSpecies } from '@/model/Animal.model';
import type { MetadataField } from '@/model/Metadata.model';
import { featuresSelector } from '@/support/Selectors';

import Spreadsheet from '@/components/UI/SpreadSheet/Spreadsheet';
import type { SpreadsheetSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import { defaultMetadataToColumnSettingMapper } from '@/support/hot';
import type { EntityFieldRequiredMap } from '@/utils/entitySchema/useEntitySchema';

import { initAnimalSettings } from './../Colony.utils';

const unknownSexSelector = createSelector([featuresSelector], (features) => ({
  unknownSexEnabled: features?.unknown_sex === true,
}));

interface AnimalsTableProps {
  data: Array<Record<string, unknown>>;
  innerRef: RefObject<HotTableRef | null>;
  metadata: Array<MetadataField>;
  setMetadata: (metadata: Array<MetadataField>) => void;
  setInvalid: (invalid: boolean) => void;
  overlay?: ReactNode;
  maxRows?: number;
  species: Array<AnimalSpecies>;
  entityFieldRequiredMap: EntityFieldRequiredMap;
  existingSettings?: SpreadsheetSettings;
  setSettings?: (settings: SpreadsheetSettings) => void;
}

const AnimalsTable: FC<AnimalsTableProps> = ({
  data,
  innerRef,
  metadata,
  setMetadata,
  setInvalid,
  overlay,
  maxRows,
  species,
  entityFieldRequiredMap,
  existingSettings,
  setSettings,
}) => {
  const { unknownSexEnabled } = useSelector(unknownSexSelector);
  const settings = useMemo<SpreadsheetSettings>(
    () =>
      initAnimalSettings(
        species,
        true,
        false,
        undefined,
        undefined,
        existingSettings,
        entityFieldRequiredMap,
        unknownSexEnabled
      ).settings,
    [species, entityFieldRequiredMap, existingSettings, unknownSexEnabled]
  );

  return (
    <Spreadsheet
      className="mt3"
      testPrefix="colony-creation-animals"
      data={data}
      innerRef={innerRef}
      settings={settings}
      setSettings={setSettings}
      metadata={metadata}
      setMetadata={setMetadata}
      metadataToColumnSettingMapper={defaultMetadataToColumnSettingMapper}
      setInvalid={setInvalid}
      overlay={overlay}
      maxRows={maxRows}
    />
  );
};

export default AnimalsTable;
